import { Card, Col, DatePicker, Empty, Row, Select, Statistic, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { financeApi, type BankBookRow } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: string | number) => Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function FinanceBankBookPage() {
  const [bankId, setBankId] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: () => financeApi.listBankAccounts(),
  });

  const bookQuery = useQuery({
    queryKey: ['finance', 'bank-book', bankId, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () =>
      financeApi.bankBook(bankId!, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')),
    enabled: !!bankId,
  });

  // Default-select the first cash account so the page lands ready-to-read.
  if (!bankId && banksQuery.data && banksQuery.data.length > 0) {
    const firstCash = banksQuery.data.find((b) => b.cash && b.active);
    setBankId(firstCash?.id ?? banksQuery.data[0].id);
  }

  const data = bookQuery.data;

  const columns: TableProps<BankBookRow>['columns'] = [
    { title: 'Date', dataIndex: 'entryDate', width: 110, render: (d: string) => dayjs(d).format('D MMM YYYY') },
    {
      title: 'Particulars',
      render: (_, row) => (
        <div>
          <Typography.Text>{row.description ?? row.accountName ?? '—'}</Typography.Text>
          {row.accountName && row.description && row.accountName !== row.description && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.accountName}
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'sourceType',
      width: 130,
      render: (v: string, row) => (
        <>
          <Tag color={row.reversal ? 'purple' : 'default'}>{v}</Tag>
          {row.reversal && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                reversal
              </Typography.Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Receipts (DR)',
      dataIndex: 'amount',
      width: 130,
      align: 'right' as const,
      render: (v: string, row) => (row.entryType === 'DEBIT' ? <Typography.Text>{PKR(v)}</Typography.Text> : '—'),
    },
    {
      title: 'Payments (CR)',
      dataIndex: 'amount',
      width: 130,
      align: 'right' as const,
      render: (v: string, row) => (row.entryType === 'CREDIT' ? <Typography.Text>{PKR(v)}</Typography.Text> : '—'),
    },
    {
      title: 'Balance',
      dataIndex: 'runningBalance',
      width: 140,
      align: 'right' as const,
      render: (v: string) => <Typography.Text strong>₨ {PKR(v)}</Typography.Text>,
    },
  ];

  const isCash = data?.cash;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Finance / Reports"
        title={isCash === undefined ? 'Cash & Bank book' : isCash ? 'Cash book' : 'Bank book'}
        description="Chronological view of one cash or bank account with running balance. Receipts on the left, payments on the right; closing balance carries forward."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/bank-book.xlsx',
              params: bankId
                ? { bankAccountId: bankId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') }
                : {},
              fileName: `${data?.cash ? 'cash' : 'bank'}-book-${range[0].format('YYYY-MM-DD')}-to-${range[1].format('YYYY-MM-DD')}.xlsx`,
              disabled: !data || !bankId,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Cash / bank account
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose account"
              loading={banksQuery.isLoading}
              value={bankId}
              onChange={setBankId}
              options={(banksQuery.data ?? [])
                .filter((b) => b.active)
                .map((b) => ({
                  value: b.id,
                  label: b.cash ? `${b.accountName} (cash)` : b.accountName,
                }))}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
              allowClear={false}
            />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={6}>
              <Statistic title="Opening balance" value={`₨ ${PKR(data.openingBalance)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Receipts in period" value={`₨ ${PKR(data.totalDebits)}`} styles={{ content: { color: '#389e0d' } }} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Payments in period" value={`₨ ${PKR(data.totalCredits)}`} styles={{ content: { color: '#cf1322' } }} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Closing balance" value={`₨ ${PKR(data.closingBalance)}`} />
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        {data && data.rows.length === 0 ? (
          <Empty description="No entries in this period" />
        ) : (
          <Table<BankBookRow>
            rowKey="entryId"
            dataSource={data?.rows ?? []}
            loading={bookQuery.isFetching}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            size="small"
            scroll={{ x: 900 }}
          />
        )}
      </Card>
    </div>
  );
}
